import { AccessTime, CheckCircle } from "@mui/icons-material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getFirstLetter } from "../../../components/display/EntityCard";
import SimpleAlert from "../../../components/display/SimpleAlert";
import CreateAppointmentFullScreenMessage from "../../../components/fullScreenMessage/CreateAppointmentFullScreenMessage";
import Skeletons from "../../../components/loader/Skeletons";
import PageContentWrapper from "../../../components/wrappers/PageContentWrapper";
import { APPOINTEMENT_DETAILS, petViewUrl } from "../../../enums/UrlPaths";
import { useFetch } from "../../../hooks/Fetching";
import useEntityTranslation from "../../../hooks/useEntityTranslation";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";
import { getDateObject } from "../../../utils/DateTimeRepository";
import { isArrayEmpty, trimText } from "../../../utils/Functions";
import {
  avatarColorCoding,
  getGenderAvatarTextColor,
} from "../../pets/utils/Functions";
import { STATUSES } from "../../../enums/AppointmentStatus";
import usePetAvatar from "../../../hooks/usePetAvatar";
import ChooseAClinicFullScreenMessage from "../../../components/fullScreenMessage/ChooseAClinicFullScreenMessage";

export function PetAppointmentCard({
  appointment,
  cardVariant = "elevation",
  cardMinHeight,
  statusTextMinHeight,
}) {
  const service = useEntityTranslation(appointment?.priceList);
  const color = usePrimaryColor();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile } = useUser();
  const { mode } = useTheme().palette;
  const hasPet = appointment?.petId != null;
  const { avatar, isLoading, error } = usePetAvatar(
    hasPet ? appointment.petId : null,
  );

  function goToPet() {
    if (!hasPet) return;
    navigate(petViewUrl(appointment.petId, profile.id));
  }

  function goToAppointment() {
    navigate(APPOINTEMENT_DETAILS + appointment.appointmentId);
  }

  return (
    <Card
      sx={{ borderRadius: 2, minHeight: cardMinHeight }}
      variant={cardVariant}
    >
      <Grid container alignItems={"center"}>
        <Grid item xs={10}>
          <CardHeader
            onClick={() => goToPet()}
            sx={{ minHeight: 82, cursor: hasPet ? "pointer" : "default" }}
            avatar={
              hasPet ? (
                <Avatar
                  src={avatar}
                  sx={{
                    width: 50,
                    height: 50,
                    color: getGenderAvatarTextColor(
                      appointment.petGender,
                      mode,
                    ),
                    bgcolor: avatarColorCoding(appointment.petGender, mode),
                  }}
                >
                  {" "}
                  {getFirstLetter(appointment.petName)}{" "}
                </Avatar>
              ) : null
            }
            title={
              <Typography
                variant="h6"
                sx={{ cursor: hasPet ? "pointer" : "default" }}
              >
                {" "}
                {hasPet
                  ? trimText(String(appointment.petName), 15)
                  : t("NoPet")}{" "}
              </Typography>
            }
          />
        </Grid>
        <Grid item>
          <Box>
            {hasPet ? (
              <IconButton
                size="small"
                variant="outlined"
                onClick={() => goToPet()}
                title={t("OpenPetDetails")}
              >
                <OpenInNewIcon color="info" />
              </IconButton>
            ) : null}
          </Box>
        </Grid>
      </Grid>
      <Divider />
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" sx={{ color: color }}>
              {t("Date")}
            </Typography>
            <Typography variant="body2">
              {dayjs(getDateObject(appointment?.startDate)).format(
                "DD/MM/YYYY",
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: color }}>
              {t("Time")}
            </Typography>
            <Typography variant="body2">
              {`${dayjs(getDateObject(appointment.startDate)).format("HH:mm")} -
                ${dayjs(getDateObject(appointment.endDate)).format("HH:mm")}`}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: color }}>
              {t("Service")}
            </Typography>
            <Typography variant="body2">{service}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: color }}>
              {t("Location")}
            </Typography>
            <Typography variant="body2">
              {appointment.clinicName} - {appointment.clinicAddress}
            </Typography>
          </Box>
          <Box sx={{ minHeight: statusTextMinHeight }}>
            <Typography variant="caption" sx={{ color: color }}>
              {t("Status")}
            </Typography>
            <Typography
              fontWeight={"bold"}
              variant="body2"
              sx={{
                color:
                  appointment.status === STATUSES.WAITING_CLINIC ||
                  appointment.status === STATUSES.WAITING_USER
                    ? "warning.main"
                    : appointment.status === STATUSES.ACTIVE
                      ? "#38b000"
                      : color,
              }}
            >
              {appointment.status === STATUSES.ACTIVE
                ? t("Accepted").toUpperCase()
                : t(STATUSES.WAITING_CLINIC).toUpperCase()}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon color="info" />}
              sx={{ borderRadius: 2 }}
              onClick={() => goToAppointment()}
            >
              {t("ViewAppointment")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function UserMobileHomePage() {
  // Set default to both "Active" and "Pending"
  const [showFilter, setShowFilter] = React.useState([
    STATUSES.ACTIVE,
    STATUSES.WAITING_CLINIC,
  ]);
  const { currentClinic } = useUser();
  const { t } = useTranslation();
  const {
    data: appointments,
    error,
    isLoading,
  } = useFetch(
    "/appointments/appointment/homepage/mobile/dto",
    null,
    "user_homepage_dto",
  );

  const filtered = getAppointments(appointments, showFilter);

  const filters = [
    {
      label: "AcceptedPlural",
      value: STATUSES.ACTIVE,
      xs: 5.8,
      icon: <CheckCircle />,
    },
    {
      label: "Pending",
      value: STATUSES.WAITING_CLINIC,
      xs: 5.8,
      icon: <AccessTime />,
    },
  ];

  if (isLoading) {
    return (
      <PageContentWrapper>
        <Skeletons numOfSkeletons={1} height={500} />
      </PageContentWrapper>
    );
  }

  if (!currentClinic.id && !isLoading) {
    return (
      <PageContentWrapper>
        <ChooseAClinicFullScreenMessage />
      </PageContentWrapper>
    );
  }

  if (isArrayEmpty(appointments) && !isLoading) {
    return (
      <PageContentWrapper marginTop={30}>
        <CreateAppointmentFullScreenMessage centerRelative={true} />
      </PageContentWrapper>
    );
  }

  if (error) {
    return (
      <PageContentWrapper>
        <SimpleAlert
          message={"ErrorFetchingAppointmentList"}
          severity={"error"}
        />
      </PageContentWrapper>
    );
  }

  return (
    <PageContentWrapper>
      <Stack spacing={2}>
        <Grid container gap={1} justifyContent={"space-between"}>
          {filters.map(({ label, value, xs, icon }) => {
            const isSelected = showFilter.includes(value);
            return (
              <Grid item xs={xs} key={label}>
                <Chip
                  label={t(label)}
                  icon={icon}
                  onClick={() => {
                    setShowFilter((prev) =>
                      prev.includes(value)
                        ? prev.filter((filter) => filter !== value)
                        : [...prev, value],
                    );
                  }}
                  sx={{
                    width: "100%",
                    textAlign: "center",
                  }}
                  className={isSelected ? "Mui-selected" : ""}
                />
              </Grid>
            );
          })}
        </Grid>
        {isArrayEmpty(filtered) ? (
          <SimpleAlert message={"PleaseSelectStatusFilter"} severity="info" />
        ) : null}
        <Stack spacing={2}>
          {filtered.map((appointment) => (
            <PetAppointmentCard
              key={appointment.id}
              appointment={appointment}
            />
          ))}
        </Stack>
      </Stack>
    </PageContentWrapper>
  );
}

function getAppointments(appointments, selectedFilters) {
  if (selectedFilters.length === 0) {
    return [];
  } else {
    return Array.isArray(appointments)
      ? appointments.filter(({ status }) =>
          selectedFilters.includes(String(status).toUpperCase()),
        )
      : [];
  }
}

